const redIcon = '/images/common/location-pin-red.svg';
const redIconHover = '/images/common/location-pin-red-hover.svg';
const blueIcon = '/images/common/location-pin-blue.svg';
const blueIconHover = '/images/common/location-pin-blue-hover.svg';
const userIcon = '/images/common/user-pin.svg';

const baseConfig = {
  showDashboard: true,
  padding: 43,
  zoom: 19,
  selectedPinZoom: 18,
  selectedPinAnimationDuration: 500,
  clusterBadgeMaxZoom: 18,
  enableClickableLogo: false,
};

const pinBaseConfig = {
  icon: redIcon,
};

const pinIconVariantPaths = {
  red: redIcon,
  redHover: redIconHover,
  blue: blueIcon,
  blueHover: blueIconHover,
};

const pinIconVariantColours = {
  red: {
    fill: '#e1261c',
    stroke: '#a01e21',
  },
  redHover: {
    fill: '#f04a42',
    stroke: '#a01e21',
  },
  blue: {
    fill: '#011e41',
    stroke: '#bbc1ce',
  },
  blueHover: {
    fill: '#123a67',
    stroke: '#bbc1ce',
  },
};

const clusterIconCache = new Map();

const getClusterText = clusterCount => {
  const count = Number(clusterCount);

  if (!Number.isFinite(count) || count <= 1) {
    return undefined;
  }

  if (count >= 1000) {
    const roundedCount =
      count >= 10000 ? Math.round(count / 1000) : Math.round(count / 100) / 10;

    return `${String(roundedCount).replace(/\.0$/, '')}k`;
  }

  return String(count);
};

const getClusterTextSize = text => {
  if (text.length <= 2) {
    return 9;
  }

  if (text.length === 3) {
    return 8;
  }

  return 7;
};

const createClusterPinIcon = (clusterCount, variant = 'red') => {
  const clusterText = getClusterText(clusterCount);
  const variantColours =
    pinIconVariantColours[variant] || pinIconVariantColours.red;
  const cacheKey = `${variant}-${clusterText}`;

  if (!clusterText) {
    return pinIconVariantPaths[variant] || redIcon;
  }

  if (clusterIconCache.has(cacheKey)) {
    return clusterIconCache.get(cacheKey);
  }

  const textSize = getClusterTextSize(clusterText);
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="46" height="52" viewBox="0 0 46 52">
      <ellipse cx="25" cy="48" rx="12" ry="3" fill="#757575" opacity="0.3"/>
      <path d="M25 4C16.7 4 10 10.7 10 19c0 11 15 27 15 27s15-16 15-27C40 10.7 33.3 4 25 4z" fill="${variantColours.fill}" stroke="${variantColours.stroke}" stroke-width="1.4"/>
      <circle cx="25" cy="19" r="8" fill="#ffffff" opacity="0.95"/>
      <circle cx="13" cy="12" r="11" fill="#f04a42" stroke="#ffffff" stroke-width="2"/>
      <text x="13" y="15.2" text-anchor="middle" font-family="Arial, sans-serif" font-size="${textSize}" font-weight="700" fill="#ffffff">${clusterText}</text>
    </svg>`;
  const icon = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

  clusterIconCache.set(cacheKey, icon);

  return icon;
};

const getPinIcon = (pin, variant = 'red', showClusterBadge = true) => {
  if (showClusterBadge && getClusterText(pin?.clusterCount)) {
    return createClusterPinIcon(pin.clusterCount, variant);
  }

  return pinIconVariantPaths[variant] || redIcon;
};

const getPinAnchor = (pin, showClusterBadge = true) => {
  if (
    !getClusterText(pin?.clusterCount) ||
    typeof window?.Microsoft?.Maps?.Point !== 'function'
  ) {
    return undefined;
  }

  return showClusterBadge
    ? new window.Microsoft.Maps.Point(25, 46)
    : new window.Microsoft.Maps.Point(18, 43);
};

/**
 * The map class constructor takes a config object and an array of locations
 * config: {
 *  container: the element in which to render the map
 *  center: a location object for the center pin
 *  handleMapMovement: function to be executed when the map is moved
 * },
 * pushpins: [{ latitude, longitude }]
 */
class MapService {
  map = null;
  currentlySelected = null;
  plottedPins = [];
  latestBound = null;
  hasMapViewChangedOnce = false;
  mouseDownOnPin = false;
  selectedPinAnimationFrame = null;

  constructor(config, pushpins) {
    this.config = config;
    this.pushpinObjects = pushpins || [];

    this.init();
  }

  init() {
    if (this.map) {
      return;
    }

    // we want to make sure we have the maps on the window
    // before we initiate
    if (
      !window?.Microsoft?.Maps ||
      !window?.Microsoft?.Maps?.Location ||
      !window?.Microsoft?.Maps?.Pushpin ||
      !window?.Microsoft?.Maps?.LocationRect
    ) {
      setTimeout(() => {
        this.init();
      }, 500);
    } else {
      this.createMap();
    }
  }

  createLocation(location) {
    return new window.Microsoft.Maps.Location(
      location.latitude,
      location.longitude
    );
  }

  shouldShowClusterBadge() {
    const currentZoom = this.map?.getZoom?.();
    const maxClusterBadgeZoom =
      this.config.clusterBadgeMaxZoom || baseConfig.clusterBadgeMaxZoom;

    if (!Number.isFinite(currentZoom)) {
      return true;
    }

    return currentZoom < maxClusterBadgeZoom;
  }

  getPinVariant(pin) {
    if (pin?.isSelected) {
      return pin?.isHovered ? 'blueHover' : 'blue';
    }

    return pin?.isHovered ? 'redHover' : 'red';
  }

  getPinDisplayOptions(pin, variant = this.getPinVariant(pin)) {
    const showClusterBadge = this.shouldShowClusterBadge();
    const anchor = getPinAnchor(pin, showClusterBadge);

    return {
      icon: getPinIcon(pin, variant, showClusterBadge),
      ...(anchor ? { anchor } : {}),
    };
  }

  createPin(pin) {
    const location = this.createLocation(pin);

    return new window.Microsoft.Maps.Pushpin(location, {
      ...pinBaseConfig,
      ...this.getPinDisplayOptions(pin, 'red'),
      ...(pin.options || {}),
    });
  }

  createView() {
    // ✅ Filter invalid pins first
    const validPins = this.pushpinObjects.filter(
      pin =>
        typeof pin.latitude === 'number' && typeof pin.longitude === 'number'
    );

    // ✅ No pins → don’t call LocationRect
    if (validPins.length === 0) {
      // fallback: don’t set bounds at all
      return {
        center: new window.Microsoft.Maps.Location(51.50735, -0.12776), // Default set London
        zoom: this.config.zoom, // city-level
      };
    }

    // ✅ Single pin logic
    if (validPins.length === 1 && this.config.zoom) {
      return {
        zoom: this.getZoomLevel(this.config.zoom, validPins[0].latitude),
        center: this.createLocation(validPins[0]),
      };
    }

    const locations = validPins.map(this.createLocation.bind(this));
    const rect = window.Microsoft.Maps.LocationRect.fromLocations(locations);

    return { bounds: rect };
  }

  createMap() {
    if (this.map instanceof window.Microsoft.Maps.Map) {
      return;
    }

    const config = {
      ...baseConfig,
      navigationBarMode: window.Microsoft.Maps.NavigationBarMode.minified,
    };

    const mapRect = this.createView();

    this.map = new window.Microsoft.Maps.Map(this.config.container, {
      ...config,
      ...mapRect,
    });

    this.setMapListeners();
    this.addPins();
  }

  setMapListeners() {
    if (!this.map) {
      return;
    }

    if (this.config.handleMapMovement) {
      window.Microsoft.Maps.Events.addHandler(this.map, 'mouseup', () => {
        if (this.mouseDownOnPin) {
          return;
        }

        this.config.handleMapMovement();
      });
    }

    window.Microsoft.Maps.Events.addHandler(this.map, 'viewchangeend', () => {
      this.updateClusterPinVisibility();
    });
  }

  addPins(pins) {
    if (pins) {
      this.pushpinObjects = [...pins];
    }

    if (!this.map || this.pushpinObjects.length === 0) {
      return;
    }

    // if the config has the user location we want to show it
    if (this.config.center) {
      // create a pushpin for the user location
      this.pushpinObjects.push({
        id: 'user-location',
        ...this.config.center,
        options: {
          icon: userIcon,
        },
      });
    }

    this.pushpinObjects.forEach(({ handleClick, id, ...singlePin }) => {
      const newPin = this.createPin(singlePin);
      newPin.isSelected = false;
      newPin.isHovered = false;
      newPin.isUserPin = id === 'user-location'; // ✅ mark user pin
      newPin.clusterCount = singlePin.clusterCount;

      this.plottedPins.push(newPin);
      this.map.entities.push(newPin);

      if (typeof handleClick === 'function') {
        window.Microsoft.Maps.Events.addHandler(newPin, 'click', handleClick);
      }

      // we need to discern between clicks on the map and clicks on the pins
      // not to trigger the map handler if a pushpin is clicked
      window.Microsoft.Maps.Events.addHandler(newPin, 'mousedown', () => {
        this.mouseDownOnPin = true;
      });
      window.Microsoft.Maps.Events.addHandler(newPin, 'mouseup', () => {
        setTimeout(() => {
          this.mouseDownOnPin = false;
        }, 100);
      });
      if (!newPin.isUserPin) {
        window.Microsoft.Maps.Events.addHandler(newPin, 'mouseover', () => {
          newPin.isHovered = true;
          newPin.setOptions(this.getPinDisplayOptions(newPin));
        });

        window.Microsoft.Maps.Events.addHandler(newPin, 'mouseout', () => {
          newPin.isHovered = false;
          newPin.setOptions(this.getPinDisplayOptions(newPin));
        });
      }
    });

    const bounds = this.createView();
    this.map.setView(bounds);
  }

  updateClusterPinVisibility() {
    this.plottedPins.forEach(pin => {
      if (getClusterText(pin?.clusterCount)) {
        pin.setOptions(this.getPinDisplayOptions(pin));
      }
    });
  }

  removePins() {
    if (!this.map) {
      return;
    }

    this.cancelSelectedPinAnimation();
    this.pushpinObjects = [];
    this.plottedPins = [];
    this.map.entities.clear();
  }

  bringPinToFront(pin) {
    if (!pin || !this.map?.entities?.remove) {
      return;
    }

    this.map.entities.remove(pin);
    this.map.entities.push(pin);
  }

  getSelectedPinZoom() {
    const configuredZoom =
      this.config.selectedPinZoom || baseConfig.selectedPinZoom;
    const currentZoom = this.map?.getZoom?.();

    if (Number.isFinite(currentZoom)) {
      return Math.max(currentZoom, configuredZoom);
    }

    return configuredZoom;
  }

  cancelSelectedPinAnimation() {
    if (
      this.selectedPinAnimationFrame &&
      typeof window?.cancelAnimationFrame === 'function'
    ) {
      window.cancelAnimationFrame(this.selectedPinAnimationFrame);
    }

    this.selectedPinAnimationFrame = null;
  }

  easeInOutCubic(progress) {
    return progress < 0.5
      ? 4 * progress * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 3) / 2;
  }

  getSelectedPinAnimationDuration() {
    return (
      this.config.selectedPinAnimationDuration ||
      baseConfig.selectedPinAnimationDuration
    );
  }

  moveToSelectedPin(center, zoom) {
    this.cancelSelectedPinAnimation();

    const requestAnimationFrame = window?.requestAnimationFrame;
    const startCenter = this.map?.getCenter?.();
    const startZoom = this.map?.getZoom?.();

    const startLatitude = Number(startCenter?.latitude);
    const startLongitude = Number(startCenter?.longitude);
    const startZoomLevel = Number(startZoom);
    const targetLatitude = Number(center?.latitude);
    const targetLongitude = Number(center?.longitude);
    const targetZoomLevel = Number(zoom);

    if (
      typeof requestAnimationFrame !== 'function' ||
      !Number.isFinite(startLatitude) ||
      !Number.isFinite(startLongitude) ||
      !Number.isFinite(startZoomLevel) ||
      !Number.isFinite(targetLatitude) ||
      !Number.isFinite(targetLongitude) ||
      !Number.isFinite(targetZoomLevel)
    ) {
      this.map.setView({ center, zoom, animate: true });
      this.updateClusterPinVisibility();
      return;
    }

    const duration = this.getSelectedPinAnimationDuration();
    const startTime = window?.performance?.now?.() || Date.now();

    const animate = timestamp => {
      const elapsed = Math.max(timestamp - startTime, 0);
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = this.easeInOutCubic(progress);
      const latitude =
        startLatitude + (targetLatitude - startLatitude) * easedProgress;
      const longitude =
        startLongitude + (targetLongitude - startLongitude) * easedProgress;
      const zoomLevel =
        startZoomLevel + (targetZoomLevel - startZoomLevel) * easedProgress;

      this.map.setView({
        center: new window.Microsoft.Maps.Location(latitude, longitude),
        zoom: zoomLevel,
        animate: false,
      });

      if (progress < 1) {
        this.selectedPinAnimationFrame = requestAnimationFrame(animate);
        return;
      }

      this.updateClusterPinVisibility();
      this.selectedPinAnimationFrame = null;
    };

    this.selectedPinAnimationFrame = requestAnimationFrame(animate);
  }

  setSelected(id) {
    // if no id is passed we want to deselect the currently selected pin
    if (!id) {
      this.cancelSelectedPinAnimation();
      this.currentlySelected?.setOptions(
        this.getPinDisplayOptions(this.currentlySelected, 'red')
      );
      if (this.currentlySelected) {
        this.currentlySelected.isSelected = false;
        this.currentlySelected.isHovered = false;
      }
      this.currentlySelected = null;

      if (this.latestBound) {
        this.map.setView({ bounds: this.latestBound });
        this.latestBound = null;
      }

      return;
    }

    // save the overview bounds once so clearing a selection can restore them
    if (!this.currentlySelected && !this.latestBound) {
      this.latestBound = this.map.getBounds();
    }

    // get the index by id
    const pinIndex = this.pushpinObjects.findIndex(pin => pin.id === id);

    if (pinIndex === -1) {
      return;
    }

    // find the Pushpin object to compare to the one on the map
    const pinToUpdate = this.plottedPins[pinIndex];
    const entityToUpdate = this.map.entities.indexOf(pinToUpdate);

    // clear the previous selected state
    this.currentlySelected?.setOptions(
      this.getPinDisplayOptions(this.currentlySelected, 'red')
    );
    if (this.currentlySelected) {
      this.currentlySelected.isSelected = false;
      this.currentlySelected.isHovered = false;
    }

    // update with the new selected state
    this.currentlySelected = this.plottedPins[pinIndex];
    this.currentlySelected.isSelected = true;
    this.map.entities
      .get(entityToUpdate)
      ?.setOptions(this.getPinDisplayOptions(this.currentlySelected, 'blue'));
    this.bringPinToFront(this.currentlySelected);

    // zoom and center the map so nearby pins separate visually
    const selectedPin = this.pushpinObjects[pinIndex];
    const center = this.createLocation(selectedPin);
    this.moveToSelectedPin(center, this.getSelectedPinZoom());
  }

  // we're using a formula to calculate the zoom
  // using the space in miles
  // see: https://stackoverflow.com/a/50743144
  getZoomLevel(radiusInMiles, latitude) {
    const rangeInMeters = radiusInMiles * 1600;
    // we take the lower value of the bounding rect of the map, so the area will be best seen
    const limitBoundPixels = Math.min(
      this.config.container.offsetHeight,
      this.config.container.offsetWidth
    );

    // if the element is hidden we return a reasonable default
    if (!limitBoundPixels) {
      return 15;
    }

    const zoom = Math.floor(
      Math.log(
        (156543.03392 * Math.cos((latitude * Math.PI) / 180)) /
          (rangeInMeters / limitBoundPixels)
      ) / Math.log(2)
    );

    return zoom;
  }

  reset() {
    this.cancelSelectedPinAnimation();
    this.map?.dispose();
  }
}

export default MapService;
